import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Autocomplete,
  TextField,
  CircularProgress,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Avatar,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import residentService from "../services/residentService";
import { useTenant } from "../contexts/TenantContext";
import { ResidentDTO } from "../types/resident.types";
import ResidentProgressNoteForm from "../components/ResidentProgressNoteForm";
import clinicalNoteService from "../services/clinicalNoteService";
import { formatToUIDateTime, formatToUIDateTimeLong } from "../utils/dateUtils";
import DailyMedicationForm from "../components/DailyMedicationForm";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

const robustParse = (d: any): number => {
  if (!d) return 0;
  let s = d;
  if (typeof d === 'string' && d.endsWith('Z') && (d.includes('T') || d.includes('-'))) {
    s = d.slice(0, -1);
  }
  const date = new Date(s);
  return date.getTime() || 0;
};

const robustFormat = (d: any): string => {
  if (!d) return "Unknown";
  let s = d;
  if (typeof d === 'string' && d.endsWith('Z') && (d.includes('T') || d.includes('-'))) {
    s = d.slice(0, -1);
  }
  return formatToUIDateTimeLong(s);
};

const ResidentProgressNotesPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const { user } = useAuth();
  const [residents, setResidents] = useState<ResidentDTO[]>([]);
  const [selectedResident, setSelectedResident] = useState<ResidentDTO | null>(null);
  const [residentsLoading, setResidentsLoading] = useState(false);
  const lastFetchedResidentId = React.useRef<string | null>(null);

  const fetchResidents = async () => {
    if (!tenant?.id) return;
    setResidentsLoading(true);
    try {
      const data = await residentService.getActiveLookup(tenant.id);
      setResidents(data || []);
    } catch (err) {
      console.error("Failed to fetch residents", err);
    } finally {
      setResidentsLoading(false);
    }
  };

  useEffect(() => {
    fetchResidents();
  }, [tenant]);

  const [searchInput, setSearchInput] = useState("");
  const [progressNotes, setProgressNotes] = useState<any[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [editingNote, setEditingNote] = useState<any | null>(null);
  const [noteToDelete, setNoteToDelete] = useState<any | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Sort residents alphabetically by residentName, falling back to first/last name
  const sortedResidents = useMemo(() => {
    return [...residents].sort((a: any, b: any) => {
      const nameA = (a.residentName || `${a.firstName || ""} ${a.lastName || ""}`).toLowerCase().trim();
      const nameB = (b.residentName || `${b.firstName || ""} ${b.lastName || ""}`).toLowerCase().trim();
      return nameA.localeCompare(nameB);
    });
  }, [residents]);

  const fetchProgressNotes = async (force: boolean = false) => {
    if (!selectedResident?.id) return;
    if (!force && selectedResident.id === lastFetchedResidentId.current && progressNotes.length > 0) return;

    const isInitialLoad = selectedResident.id !== lastFetchedResidentId.current;
    if (isInitialLoad) {
      setLoadingNotes(true);
      setProgressNotes([]);
    }

    try {
      lastFetchedResidentId.current = selectedResident.id;
      // Fetch only from EMR progress notes as requested
      const emrNotes = await clinicalNoteService.getProgressNotesByResident(selectedResident.id);

      const allNotes = [...(emrNotes || [])];

      // Sort by date descending using robust date parsing
      const sortedNotes = allNotes.sort((a, b) => {
        const dateA = robustParse(a.dateTime || a.noteDate || a.createdAt || 0);
        const dateB = robustParse(b.dateTime || b.noteDate || b.createdAt || 0);
        return dateB - dateA;
      });

      // De-duplicate if same ID appears in both (unlikely but safe)
      const uniqueNotes = Array.from(new Map(sortedNotes.map(n => [n.id || (n as any)._id || Math.random(), n])).values());

      setProgressNotes(uniqueNotes);
    } catch (error) {
      console.error("Failed to fetch progress notes:", error);
    } finally {
      setLoadingNotes(false);
    }
  };

  useEffect(() => {
    if (selectedResident) {
      fetchProgressNotes();
    } else {
      setProgressNotes([]);
    }
  }, [selectedResident]);

  // Handle resident pre-selection from location state
  useEffect(() => {
    const stateResidentId = (location.state as any)?.residentId;
    if (stateResidentId && !selectedResident && residents.length > 0) {
      const found = residents.find((r) => String(r.id) === String(stateResidentId));
      if (found) {
        setSelectedResident(found);
        // Clear state only after selection to prevent re-runs
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [location.state, residents, selectedResident, navigate]);


  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const newNotes = progressNotes.filter((n) => {
    const d = new Date(n.dateTime || n.noteDate || n.createdAt);
    return d.getTime() >= today.getTime();
  });
  const previousNotes = progressNotes.filter((n) => {
    const d = new Date(n.dateTime || n.noteDate || n.createdAt);
    return d.getTime() < today.getTime();
  });

  const normalizedRole = (user?.role || "").toString().toUpperCase().replace(/^ROLE_/, "").trim();
  const isSuperAdmin = normalizedRole === "SUPER_ADMIN";
  const isRestrictedRole = normalizedRole === "CAREGIVER" || normalizedRole.includes("CAREGIVER") || normalizedRole === "NURSE";

  const handleDeleteNote = async () => {
    if (!noteToDelete?.id) return;
    setIsDeleting(true);
    try {
      await clinicalNoteService.deleteProgressNote(noteToDelete.id);
      setDeleteDialogOpen(false);
      setNoteToDelete(null);
      fetchProgressNotes(true);
    } catch (error) {
      console.error("Failed to delete progress note:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const renderNoteList = (notes: any[], title: string, isPrevious: boolean = false) => {
    const isSuperOrFacilityAdmin = ["SUPER_ADMIN", "FACILITY_ADMIN", "CLIENT_ADMIN", "ADMIN", "OFFICER"].includes(normalizedRole);
    const showActions = isPrevious ? isSuperOrFacilityAdmin : true;

    return (
      <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h6" gutterBottom color="text.primary" sx={{ fontWeight: 'bold' }}>
          {title}
        </Typography>
        <Divider sx={{ mb: 1 }} />
        {loadingNotes ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : notes.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No notes found.
          </Typography>
        ) : (
          <Box sx={{ flexGrow: 1, minWidth: 0, width: '100%' }}>
            <TableContainer sx={{ overflowY: 'auto', maxHeight: '500px', overflowX: 'auto', width: '100%' }}>
              <Table size="small" stickyHeader sx={{ minWidth: 400 }}>
                <TableHead sx={{
                  "& .MuiTableCell-head": {
                    color: "#434445ff !important",


                  }
                }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: '150px', whiteSpace: 'nowrap' }}>Date & Time</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Progress Note Content</TableCell>
                    {showActions && (
                      <TableCell sx={{ fontWeight: 'bold', width: '40px' }} align="center">Actions</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {notes.map((note, index) => (
                    <TableRow key={note.id || index} hover>
                      <TableCell sx={{ verticalAlign: 'top', color: 'text.secondary', fontSize: '0.8rem' }}>
                        {(() => {
                          // Prefer the actual note timestamp on the record.
                          const d = note.dateTime || note.noteDate || note.createdAt || note.createdOn;
                          return robustFormat(d);
                        })()}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top', fontSize: '0.88rem', lineHeight: 1.65, color: '#1E293B', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {note.note || note.content || note.subject || "-"}
                      </TableCell>
                      {showActions && (
                        <TableCell sx={{ verticalAlign: 'top' }} align="center">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <Tooltip title="Edit Note">
                              <IconButton size="small" onClick={() => setEditingNote(note)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {isSuperAdmin && (
                              <Tooltip title="Delete Note">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    setNoteToDelete(note);
                                    setDeleteDialogOpen(true);
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>
    );
  };

  return (
    <Box sx={{ width: "100%", maxWidth: "100%", px: 0, pb: 2, pt: 3, }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Box sx={{ typography: 'h1', display: 'flex', alignItems: 'center' }}>
          <AssignmentIcon sx={{ fontSize: 36, color: "#48CAE4" }} />
        </Box>
        <Box>
          <Typography
            variant="h5"
            fontWeight="700"
            sx={{ mb: 0.5, color: '#1A2B45' }}
          >
            Resident Progress Notes
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.85rem" }}>
            Review and manage clinical documentation and nursing notes
          </Typography>
        </Box>
      </Box>

      {/* ── Controls Bar ── */}
      <Box sx={{
        bgcolor: "#FFFFFF",
        borderRadius: "16px",
        border: "1px solid #E2E8F0",
        p: 2,
        mb: 3,
        display: "flex",
        alignItems: "center",
        gap: 2,
        flexWrap: "wrap"
      }}>
        <Autocomplete
          id="resident-selector"
          options={sortedResidents.filter(r => r?.status?.toUpperCase() === "ACTIVE")}
          getOptionLabel={(option) =>
            `${(option as any).residentName || `${option.firstName} ${option.lastName}`} ${option.roomNumber ? `(Room: ${option.roomNumber}${option.bedNumber ? `, Bed: ${option.bedNumber}` : ""})` : ""}`
          }
          loading={residentsLoading}
          value={selectedResident}
          onChange={(event: any, newValue: any | null) => {
            setSelectedResident(newValue);
          }}
          inputValue={searchInput}
          onInputChange={(event, newInputValue) => {
            setSearchInput(newInputValue);
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search Resident by Name"
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {residentsLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
          sx={{ flex: 1, minWidth: 250 }}
        />
      </Box>

      {selectedResident ? (
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          gap: 3,
          alignItems: { lg: 'flex-start' },
        }}>
          {/* Left Column: Add / Update Note Form */}
          <Box sx={{
            width: { xs: '100%', lg: '380px', xl: '420px' },
            flexShrink: 0,
          }}>
            <Paper sx={{ p: 3, borderRadius: '16px', border: '1px solid #E2E8F0', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)' }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5, color: '#1A2B45' }}>
                {editingNote ? "Update Daily Progress Note" : "Add New Progress Note"}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mb: 2, fontSize: "0.82rem" }}>
                Document daily observations, status updates and care activities
              </Typography>
              <Divider sx={{ mb: 2.5 }} />
              <ResidentProgressNoteForm
                residentId={selectedResident.id || null}
                residentName={(selectedResident as any).residentName || `${selectedResident.firstName} ${selectedResident.lastName}`}
                initialData={editingNote}
                latestNoteContent={progressNotes.length > 0 ? (progressNotes[0].note || progressNotes[0].content || progressNotes[0].subject || "") : ""}
                skipFetch={true}
                onCancel={editingNote ? () => setEditingNote(null) : undefined}
                onSuccess={() => {
                  setEditingNote(null);
                  fetchProgressNotes(true);
                }}
              />
            </Paper>
          </Box>

          {/* Right Column: Progress Notes (Wide & Spacious) */}
          <Box sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
          }}>
            {renderNoteList(newNotes, "Today's Progress Notes", false)}
            {previousNotes.length > 0 && renderNoteList(previousNotes, "Previous Progress Notes", true)}
          </Box>
        </Box>
      ) : (
        <Paper sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
          <Typography>Please select a resident to view progress notes.</Typography>
        </Paper>
      )}


      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !isDeleting && setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "20px" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1, pt: 3, px: 3 }}>
            <Avatar sx={{ bgcolor: "#FEE2E2", color: "#DC2626", borderRadius: "10px" }}><DeleteIcon /></Avatar>
            <Typography fontWeight={800} fontSize="1rem">Confirm Delete</Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 3, pt: 1 }}>
          <Typography variant="body2" sx={{ color: "#475569", lineHeight: 1.6 }}>
            Are you sure you want to delete this progress note?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={isDeleting}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, color: "#475569" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteNote}
            variant="contained"
            disabled={isDeleting}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, bgcolor: "#DC2626", "&:hover": { bgcolor: "#B91C1C" }, px: 3 }}
            startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : undefined}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ResidentProgressNotesPage;
